import { styled } from '@linaria/react';
import { Menu } from '@mantine/core';
import { useNavigate } from 'react-router';
import { icons } from '../../../assets/icons/icons';
import { Icon } from '../../../ui-library/icon/icon';
import { iconColorLogo } from '../../../ui-library/logo/assets';
import { Text } from '../../../ui-library/typography/typography';

/** Route the "Bloomfilter" entry returns to — the same target the old logo exit used. */
export const BLOOMFILTER_APP_ROUTE = '/application/strategy';

/**
 * Top-left app switcher for the full-screen Flora workspace. Shows where the
 * user is (Flora) with an unfold control; the menu lists Bloomfilter and Flora
 * so leaving Flora reads as switching to a peer workspace rather than a hidden
 * logo click.
 *
 * @returns {JSX.Element} The switcher trigger and its dropdown menu
 */
export function AgentAppSwitcher() {
  const navigate = useNavigate();
  return (
    <Menu shadow="md" radius="md" position="bottom-start" offset={6} withinPortal={false}>
      <Menu.Target>
        <SwitcherTrigger type="button" aria-label="Flora, switch app">
          <img src={icons.iconFlora} alt="" aria-hidden="true" width={32} height={32} />
          <Text size="medium" weight="bold" color="var(--ink-darkest)">
            Flora
          </Text>
          <DecorativeIcon aria-hidden="true">
            <Icon name="unfold_more" color="var(--ink-lighter)" size={18} pointer />
          </DecorativeIcon>
        </SwitcherTrigger>
      </Menu.Target>
      <SwitcherDropdown>
        <SwitcherHeading size="tiny" weight="bold" color="var(--ink-lighter)">
          Switch to
        </SwitcherHeading>
        <SwitcherItem
          leftSection={<img src={iconColorLogo} alt="" aria-hidden="true" width={24} height={24} />}
          rightSection={
            <DecorativeIcon aria-hidden="true">
              <Icon name="arrow_forward" color="var(--ink-lighter)" size={16} pointer />
            </DecorativeIcon>
          }
          onClick={() => navigate(BLOOMFILTER_APP_ROUTE)}
        >
          <AppLabel>
            <Text size="small" weight="bold" color="var(--ink-dark)">
              Bloomfilter
            </Text>
            <Text size="tiny" color="var(--ink-lighter)">
              Dashboards, process, strategy
            </Text>
          </AppLabel>
        </SwitcherItem>
        <SwitcherItem
          aria-current="page"
          leftSection={<img src={icons.iconFlora} alt="" aria-hidden="true" width={24} height={24} />}
          rightSection={
            <DecorativeIcon aria-hidden="true">
              <Icon name="check" color="var(--secondary-base)" size={16} pointer />
            </DecorativeIcon>
          }
        >
          <AppLabel>
            <Text size="small" weight="bold" color="var(--ink-dark)">
              Flora
            </Text>
            <Text size="tiny" color="var(--ink-lighter)">
              Agentic workspace
            </Text>
          </AppLabel>
        </SwitcherItem>
      </SwitcherDropdown>
    </Menu>
  );
}

const SwitcherTrigger = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 6px 4px 4px;
  margin: -4px -6px -4px -4px;
  border: none;
  border-radius: 10px;
  background: transparent;
  cursor: pointer;
  transition: background 0.12s;

  &:hover,
  &[aria-expanded='true'] {
    background: var(--sky-light);
  }

  &:focus-visible {
    outline: 2px solid var(--secondary-base);
    outline-offset: 2px;
  }
`;

const SwitcherDropdown = styled(Menu.Dropdown)`
  background: var(--sky-lightest);
  border: 1px solid var(--sky-light);
  box-shadow: var(--box-shadow-big-soft);
  padding: 6px;
  width: 260px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SwitcherHeading = styled(Text)`
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 6px 10px 4px;
  user-select: none;
`;

// Mantine paints hover, keyboard focus and [data-menu-active] from
// --menu-item-hover; pointing it at a theme token keeps the highlight readable
// in Flora dark mode, where Mantine itself still runs its light scheme.
const SwitcherItem = styled(Menu.Item)`
  --menu-item-hover: var(--sky-lighter);
  padding: 8px 10px;
  border-radius: 6px;
  gap: 10px;
`;

const DecorativeIcon = styled.span`
  display: flex;
`;

const AppLabel = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;
